/**
 * Transport for the app-facing edge functions.
 *
 * Plain `fetch`, not `supabase.functions.invoke`: invoke only really does
 * POST-with-a-body and buries a failure's response on `error.context`. Here the
 * method, path and query are ours and `errorMessage` is read straight off the
 * response.
 */

import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/integrations/supabase/client";
import { FUNCTION_REGION, FUNCTION_REGION_PARAM } from "@/lib/function-region";
import { type ListParams, listParamsToBody, type PagedResult } from "@/lib/list-query";

export interface ApiEnvelope<T> {
  data: T | null;
  dataList: T[] | null;
  responseMessage: string | null;
  errorMessage: string | null;
  totalRecords: number | null;
  totalPageRecords: number | null;
  currentPageNumber: number | null;
}

export class ApiError extends Error {
  readonly status: number;
  /** A failure's envelope `data`: what the server says beyond the sentence —
   * on a wrong-account invitation accept, `{ invited_email }`. Undefined when it
   * sent none. */
  readonly data?: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/** A 404 — for an agent, one that was deleted: nothing to retry, and no request to send its id with. */
export const isNotFound = (error: unknown): boolean => error instanceof ApiError && error.status === 404;

/** A query's retry rule: a refusal (4xx) is the same on every try; only a failure on our side, or the network's, is worth another. */
export const retryUnlessRefused = (failures: number, error: Error) =>
  !(error instanceof ApiError && error.status < 500) && failures < 3;

interface RequestOptions {
  query?: Record<string, string | undefined>;
  body?: unknown;
}

async function request<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  options: RequestOptions = {},
): Promise<ApiEnvelope<T>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new ApiError("Not authenticated", 401);

  const url = new URL(`${SUPABASE_URL}/functions/v1${path}`);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, value);
  }
  url.searchParams.set(FUNCTION_REGION_PARAM, FUNCTION_REGION);

  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      // The gateway needs a project key to route; the bearer token above is
      // what actually authenticates the caller.
      apikey: SUPABASE_PUBLISHABLE_KEY,
      "Content-Type": "application/json",
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  const envelope = await response.json().catch(() => null) as ApiEnvelope<T> | null;

  if (!response.ok) {
    throw new ApiError(
      envelope?.errorMessage ?? `Request failed (${response.status})`,
      response.status,
      envelope?.data ?? undefined,
    );
  }
  if (!envelope) throw new ApiError("Malformed response from the server", response.status);
  return envelope;
}

function item<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.data === null) throw new ApiError("Response carried no data", 500);
  return envelope.data;
}

/** An empty list is `[]`, never null. */
function list<T>(envelope: ApiEnvelope<T>): T[] {
  return envelope.dataList ?? [];
}

/** `totalRecords` is the whole set. Falling back to the page length would size
 * every pager to a single page. */
function page<T>(envelope: ApiEnvelope<T>): PagedResult<T> {
  return {
    rows: envelope.dataList ?? [],
    total: envelope.totalRecords ?? 0,
    page: envelope.currentPageNumber ?? 1,
  };
}

export const api = {
  getItem: async <T>(path: string, options?: RequestOptions) =>
    item(await request<T>("GET", path, options)),
  /** A read whose `null` is an answer — "there is none" — not an error
   * (`cal-integration/agent/:id`: an agent with no Cal.com connection). */
  getItemOrNull: async <T>(path: string, options?: RequestOptions): Promise<T | null> =>
    (await request<T>("GET", path, options)).data,
  getList: async <T>(path: string, options?: RequestOptions) =>
    list(await request<T>("GET", path, options)),
  /** A list with no paging: the whole set in one answer (`workspace/list`). */
  postList: async <T>(path: string, body: unknown = {}) =>
    list(await request<T>("POST", path, { body })),
  /** A list is a POST: the query is a document — paging, a search term and a
   * sort — typed as itself rather than coerced through a query string. */
  postPage: async <T>(path: string, params: ListParams, filters?: Record<string, unknown>) =>
    page(await request<T>("POST", path, { body: { ...filters, ...listParamsToBody(params) } })),
  post: async <T>(path: string, body: unknown) =>
    item(await request<T>("POST", path, { body })),
  put: async <T>(path: string, body: unknown) =>
    item(await request<T>("PUT", path, { body })),
  /** Delete returns only a message. */
  del: async (path: string) => {
    await request<never>("DELETE", path);
  },
  /** A delete whose answer says more than that it happened (`billing/:id/payment-method`:
   * a reload the bank still holds). */
  delItem: async <T>(path: string) => item(await request<T>("DELETE", path)),
};
